import * as CD from 'app/cd/models/essette-api';
import { ProviderService } from 'app/cd/services/provider-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { apiCall } from 'app/shared/lib/api/api';
import { ComponentBase, SortDirection, Sorting, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { Toaster } from 'app/shared/lib/toaster';
import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { IPopup } from 'app/shared/elements/popup-base';
import { SaveContextCustomElement } from 'app/pp/components/admin/save-context';
import { validate } from 'app/shared/elements/ess-rule';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { confirmation, ConfirmationOptions } from 'app/shared/elements/ess-confirm';
import { I18N } from 'aurelia-i18n';

@autoinject()
export class UsersGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected users: ReadonlyArray<CD.ProviderUserContext> | null = null;

    protected sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 5;

    protected modalTitle: string = '';
    protected validateForm = validate;
    protected $isValid: boolean = true;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;
    protected $saveUser!: SaveContextCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly providerService: ProviderService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        this.reload();
    }


    ////****************** Events ***************************************************////
    private async reload(): Promise<void> {
        await this.loadUsers();
    }


    ////****************** Functions ************************************************////
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting: sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected editUser(userContext: CD.ProviderUserContext): void {
        if(this.$modal && this.$saveUser) {
            this.modalTitle = !!userContext?.id ? 'ui:actions.edit' : 'ui:labels.new';
            this.$saveUser.loadUserContext(userContext);
            this.$modal.open();
        }
    }

    protected handleUserSaved(event: FormSuccessCustomEvent) {
        this.$modal.close();
        this.reload();
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadUsers(): Promise<void> {
        const options: StandardLoadOptions = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: false
        };
        const includes: string[] = [
            'user',
            'homepage',
            'roles',
            'user.phoneNumber.primary',
            'user.phoneNumber.cell',
            'user.displayTimeZone',
            'providerLocations'
        ];

        try {
            this.state.beginLoad();
            const response = await this.providerService.loadProviderUserContexts(options, includes);
            this.users = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async unlinkUser(userContext: CD.ProviderUserContext): Promise<void> {

        try {
            const { dialogResponse } = await confirmation.showCustom(
                this.i18n.tr('ui:actions.unlink', { what: this.i18n.tr('core:user.$') }),
                this.i18n.tr('pp:providerUsers.messages.confirmUnlink'),
                ConfirmationOptions.OKCancel
            );
            if(dialogResponse === ConfirmationOptions.OK) {
                this.state.beginSave();
                const { isSuccessful } = await this.providerService.deactivateProviderUserContext(userContext);
                this.reload();
                // INFO [Matt] this.toaster.info() or this.toaster.error()
                this.toaster[isSuccessful ? 'info' : 'error']()
                    .message({
                        key: isSuccessful ? 'ui:messages.events.unlinked' : 'ui:messages.events.unlinkFailed',
                        what: 'core:user.$'
                    })
                    .show();
            }
        }
        catch(error) {
            this.toaster.error()
                .message({
                    key: 'ui:messages.events.unlinkFailed',
                    what: 'core:user.$'
                })
                .show();
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }
}
